import { useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { clearCache } from '@/utils/cache';

export interface HomePageConfig {
  employee_count: number;
  active_markets: number;
  rollout_status: string;
  announcement_title: string;
  announcement_body: string;
}

export const usePutHomePage = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (config: HomePageConfig): Promise<boolean> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      setError('REACT_APP_API_URL is not set');
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      let token: string | null = null;
      if (isAuthenticated && accounts.length > 0) {
        token = await acquireApiToken(instance, accounts[0]);
      }

      if (!token) {
        setError('Not authenticated');
        return false;
      }

      const response = await fetch(`${apiUrl}/admin/home-page`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(config),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      // Bust the home page stats cache so the next visit re-fetches
      clearCache('dashboardStats_v1');
      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving, error };
};
